import './CblModuleManagement.css';
import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAlgoList } from '../../hooks/queries/algoQueries';
import Pagination from '../../components/Pagination/Pagination';
import { SkeletonTable } from '../../components/Skeleton';
import dayjs from 'dayjs';
import PlusIcon from '../../assets/icons/plus.svg?react';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import EditIcon from '../../assets/icons/edit.svg?react';
import CblModuleAppendModal from './components/CblModuleAppendModal';
import CblModuleDeleteModal from './components/CblModuleDeleteModal';
import CblModuleUpdateModal from './components/CblModuleUpdateModal';
import RefreshIcon from '../../assets/icons/refresh.svg?react';

const CblModuleManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAppendModalOpen, setIsAppendModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [refreshMsg, setRefreshMsg] = useState('');
  const [keywordInput, setKeywordInput] = useState(''); // 입력 중인 키워드 (로컬 state)
  // URL 파라미터에서 상태 가져오기
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 10;
  const sortBy = searchParams.get('sortBy') || 'CreatedDate';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const keyword = searchParams.get('Keyword') || '';
  
  // keywordInput을 URL의 keyword와 동기화 (초기값 설정)
  useEffect(() => {
    if (keyword) {
      setKeywordInput(keyword);
    } else {
      setKeywordInput('');
    }
  }, [keyword]);
  
  // 필터 파라미터들
  const moduleName = searchParams.get('ModuleName') || '';
  const version = searchParams.get('Version') || '';
  const moduleType = searchParams.get('ModuleType') || '';
  const status = searchParams.get('Status') || '';

  // 쿼리 파라미터 메모이제이션 (URL 상태와 연동)
  const cblModuleParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder,
    };
    
    if (keyword) params.Keyword = keyword;
    if (moduleName) params.ModuleName = moduleName;
    if (version) params.Version = version;
    if (moduleType) params.ModuleType = moduleType;
    if (status) params.Status = status;
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, keyword, moduleName, version, moduleType, status]);

  // API 호출
  const { 
    data: cblModuleData, 
    isLoading: isLoadingCblModule,
    error: errorCblModule,
    refetch: refetchCblModule
  } = useAlgoList(cblModuleParams);

  const updateSearchParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, ...newParams });
  };

  const handlePageChange = (page) => {
    updateSearchParams({ page: page.toString() });
  };

  const handleSort = (field, order) => {
    updateSearchParams({ sortBy: field, sortOrder: order, page: '1' });
  };

  // Keyword 입력 핸들러 (로컬 state만 업데이트)
  const handleKeywordChange = (e) => {
    setKeywordInput(e.target.value);
  };

  // Keyword 입력에서 Enter 키 처리 (쿼리 실행)
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value.trim();
      const current = Object.fromEntries(searchParams.entries());
      
      if (value && value.length >= 2) {
        setSearchParams({ ...current, Keyword: value, page: '1' });
      } else {
        // 빈 값이면 Keyword 파라미터 제거
        const { Keyword, ...rest } = current;
        setSearchParams({ ...rest, page: '1' });
      }
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    return dayjs(dateTimeString).format('YYYY-MM-DD HH:mm:ss');
  };

  const handleDelete = (item) => {
    setTarget(item);
    setDeleteModalOpen(true);
  };

  const handleEdit = (item) => {
    setTarget(item);
    setUpdateModalOpen(true);
  };

  const handleSuccess = async () => {
    await refetchCblModule();
    setIsAppendModalOpen(false);
    setDeleteModalOpen(false);
    setUpdateModalOpen(false);
  };

  const handleRefresh = async () => {
    try {
      setRefreshMsg('...');
      await refetchCblModule();
      setRefreshMsg('CBL 모듈 목록 새로고침 완료!');
    } catch (error) {
      console.error('새로고침 실패:', error);
      setRefreshMsg('CBL 모듈 목록 새로고침 실패!');
    }
    finally {
      setTimeout(() => {
        setRefreshMsg('');
      }, 1500);
    }
  };

  const totalPages = cblModuleData?.Total 
    ? Math.ceil(cblModuleData.Total / pageSize) 
    : cblModuleData?.Items?.length > 0 
    ? Math.ceil(cblModuleData.Items.length / pageSize)
    : 1;

  return (
    <div className="cbl-module-management-container page-container">
      <div className="cbl-module-management-content w-full">
        <div className="flex justify-between align-center mb-5">
          <div className='flex w-half'>
            <input 
              type="text" 
              placeholder="키워드 검색 (2자 이상) - 모듈명, 버전, API 엔드포인트" 
              className="input-primary flex-1 text-b2-400" 
              value={keywordInput}
              onChange={handleKeywordChange}
              onKeyDown={handleKeywordKeyDown}
            />
          </div>
          <div className="flex justify-end align-center gap-5">
            <p className="text-b2-400 text-primary">{refreshMsg}</p>
            <button className="btn btn-style-5 big-btn flex align-center gap-3" onClick={handleRefresh}>
              <RefreshIcon color='var(--gray-600)'/>
              <p className="text-b2-400 text-gray-600">새로고침</p>
            </button>
            <button className="btn btn-primary big-btn flex align-center gap-3" onClick={() => setIsAppendModalOpen(true)}>
              <PlusIcon color='var(--white)'/>
              <p className="text-b2-400 text-white">모듈 추가</p>
            </button>
          </div>
        </div>
        {isLoadingCblModule ? (
          <SkeletonTable rows={5} cols={7} />
        ) : errorCblModule ? (
          <div className="error-message">데이터를 불러오는 중 오류가 발생했습니다.</div>
        ) : (
          <>
            <div className="cbl-module-management-table w-full">
              <table className="primary-table w-full">
                <thead>
                  <tr>
                    <th>모듈명</th>
                    <th>버전</th>
                    <th>API 엔드포인트</th>
                    <th>생성 일시</th>
                    <th>비고</th>
                    <th width="80px"></th>
                  </tr>
                </thead>
                <tbody>
                  {cblModuleData?.Items?.length > 0 ? (
                    cblModuleData.Items.map((item) => (
                      <tr key={item.CblModuleInfoSysId}>
                        <td>{item.ModuleName || '-'}</td>
                        <td>{item.Version || '-'}</td>
                        <td>{item.BaseUrl || '-'}</td>
                        <td>{formatDateTime(item.CreatedDate) || '-'}</td>
                        <td>{item.Note || '-'}</td>
                        <td>
                          <div className='flex align-center gap-5'>
                            <button className="btn icon-btn" onClick={() => handleEdit(item)}>
                              <EditIcon color='var(--gray-600)'/>
                            </button>
                            <button className="btn icon-btn" onClick={() => handleDelete(item)}>
                              <DeleteIcon color='var(--gray-600)'/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-data">
                        <p className="text-b3-700 text-gray-500">CBL 모듈 데이터가 없습니다.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination-wrapper">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
      {isAppendModalOpen && (
        <CblModuleAppendModal
          isOpen={isAppendModalOpen}
          onClose={() => setIsAppendModalOpen(false)}
          onSuccess={handleSuccess}
        />
      )}
      {deleteModalOpen && (
        <CblModuleDeleteModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          target={target}
          onSuccess={handleSuccess}
        />
      )}
      {updateModalOpen && (
        <CblModuleUpdateModal
          isOpen={updateModalOpen}
          onClose={() => setUpdateModalOpen(false)}
          target={target}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default CblModuleManagement;
